import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../support/store';
import { HbrOption, HbrSelect, HbrSpinner } from '@harbor/elements-react-standalone';

import { useOrgChart } from '../../hooks/useOrgChart';
import { useFetchCampaigns } from '../../hooks/campaign-hooks';
import { isManagerEmail, OrgChartRow } from '../../support/orgScope';
import {
  buildAttainmentOrgRows,
  currentAttainmentPeriod,
  findPeriodByLabel,
  intervalOptions,
} from '../../support/attainmentLeader';

import LeaderFiltersBar, { LeaderNavState } from './LeaderFiltersBar';
import TeamSummaryPage from './Salesforce/TeamSummaryPage';
import IndividualContributorPage from './IndividualContributorPage';
import WorkshopsTeamSummaryPage from './Workshops/WorkshopsTeamSummaryPage';
import WorkshopsIndividualContributorPage from './Workshops/WorkshopsIndividualContributorPage';
import PovTeamSummaryPage from './Pov/PovTeamSummaryPage';
import PovIndividualContributorPage from './Pov/PovIndividualContributorPage';
import FirestarterPage from './Firestarter/FirestarterPage';
import CampaignsTeamSummaryPage from './Campaigns/CampaignsTeamSummaryPage';
import CampaignsIndividualContributorPage from './Campaigns/CampaignsIndividualContributorPage';
import EnablementTeamSummaryPage from './Enablement/EnablementTeamSummaryPage';
import EnablementIndividualContributorPage from './Enablement/EnablementIndividualContributorPage';
import { useEducationCompletionByEmail } from '../../hooks/education-completion-hooks';
import {
  useAttainmentChain,
  type AttainmentDrillNode,
  type AttainmentPeriodMeta,
} from '../../hooks/attainment-hooks';
import { useFetchLeaderPages } from '../../hooks/toolbox-hooks';
import { useFiscalTimePeriod } from '../../hooks/useFiscalTimePeriod';
import { TIME_PERIOD_OPTIONS } from '../../support/fiscalTimePeriods';
import TimePeriodSelect from './TimePeriodSelect';
import { asSingleValue } from '../../support/utils';
import AttainmentIntervalBar from './Attainment/AttainmentIntervalBar';
import AttainmentTeamSummaryPage from './Attainment/AttainmentTeamSummaryPage';
import AttainmentIndividualContributorPage from './Attainment/AttainmentIndividualContributorPage';
import {
  ATTAINMENT_DOMAIN,
  resolveLeaderPages,
  deriveLeaderTabs,
  findLeaderPage,
} from '../../support/leaderPages';

const PROXY_LEADER_EMAIL = 'kyturner@cisco.com';
const GLOBAL_PROXY_PERMISSION = 'global.proxy';

// Context passed to a page renderer. Each page component needs domain-specific props/data, so the
// `component_key -> renderer` registry (below) wires them from this shared context. The DB (via
// leader_page rows) selects WHICH renderer runs for a (domain, mode); this map supplies the wiring.
interface LeaderRenderContext {
  org: OrgChartRow[];
  currentEmail: string;
  onSelectPerson: (email: string) => void;
  campaignId?: number;
  campaignName?: string;
  // Campaigns tab time dropdown (fiscal window; null = all time).
  campaignWindow: { start: string; end: string } | null;
  campaignTimeShort: string;
  campaignTimePending: boolean;
  educationNode: any;
  // Enablement tab time dropdown, as the `time_filter` (`all` or `range:…`) the education endpoints take.
  educationTimeFilter: string;
  // Drill node for the person focused on the Attainment tab (its own reporting structure).
  attainmentDrillNode: AttainmentDrillNode | null;
  // Goaling interval (e.g. H1 FY27) the Attainment tab's figures refer to.
  attainmentInterval: AttainmentPeriodMeta | null;
  // Salesforce tab time dropdown (Current Fiscal Month / Quarter / Year / All).
  timeFilter: string;
  timeShort: string;
  timePending: boolean;
  // POV tab time dropdown (same choices; opportunities are kept by Close Date).
  povTimeFilter: string;
  povTimeShort: string;
  povTimePending: boolean;
}

const LEADER_PAGE_RENDERERS: Record<string, (ctx: LeaderRenderContext) => JSX.Element> = {
  'Leader.TeamSummaryPage': (c) => (
    <TeamSummaryPage
      orgRows={c.org}
      managerEmail={c.currentEmail}
      onSelectPerson={c.onSelectPerson}
      timeFilter={c.timeFilter}
      timeShort={c.timeShort}
      timePending={c.timePending}
    />
  ),
  'Leader.IndividualContributorPage': (c) => (
    <IndividualContributorPage
      email={c.currentEmail}
      timeFilter={c.timeFilter}
      timeShort={c.timeShort}
      timePending={c.timePending}
    />
  ),
  'Leader.WorkshopsTeamSummaryPage': (c) => (
    <WorkshopsTeamSummaryPage orgRows={c.org} managerEmail={c.currentEmail} onSelectPerson={c.onSelectPerson} />
  ),
  'Leader.WorkshopsIndividualContributorPage': (c) => (
    <WorkshopsIndividualContributorPage email={c.currentEmail} />
  ),
  'Leader.PovTeamSummaryPage': (c) => (
    <PovTeamSummaryPage
      orgRows={c.org}
      managerEmail={c.currentEmail}
      onSelectPerson={c.onSelectPerson}
      timeFilter={c.povTimeFilter}
      timeShort={c.povTimeShort}
      timePending={c.povTimePending}
    />
  ),
  'Leader.PovIndividualContributorPage': (c) => (
    <PovIndividualContributorPage
      email={c.currentEmail}
      timeFilter={c.povTimeFilter}
      timeShort={c.povTimeShort}
      timePending={c.povTimePending}
    />
  ),
  'Leader.FirestarterTeamSummaryPage': (c) => <FirestarterPage orgRows={c.org} email={c.currentEmail} />,
  'Leader.CampaignsTeamSummaryPage': (c) => (
    <CampaignsTeamSummaryPage
      orgRows={c.org}
      managerEmail={c.currentEmail}
      onSelectPerson={c.onSelectPerson}
      campaignId={c.campaignId}
      campaignName={c.campaignName}
      timeWindow={c.campaignWindow}
      timeShort={c.campaignTimeShort}
      timePending={c.campaignTimePending}
    />
  ),
  'Leader.CampaignsIndividualContributorPage': (c) => (
    <CampaignsIndividualContributorPage
      email={c.currentEmail}
      orgRows={c.org}
      campaignId={c.campaignId}
      campaignName={c.campaignName}
      timeWindow={c.campaignWindow}
      timeShort={c.campaignTimeShort}
      timePending={c.campaignTimePending}
    />
  ),
  'Leader.EnablementTeamSummaryPage': (c) => (
    <EnablementTeamSummaryPage
      orgRows={c.org}
      managerEmail={c.currentEmail}
      onSelectPerson={c.onSelectPerson}
      eduNode={c.educationNode}
      timeFilter={c.educationTimeFilter}
    />
  ),
  'Leader.EnablementIndividualContributorPage': (c) => (
    <EnablementIndividualContributorPage
      email={c.currentEmail}
      eduNode={c.educationNode}
      timeFilter={c.educationTimeFilter}
    />
  ),
  'Leader.AttainmentTeamSummaryPage': (c) =>
    c.attainmentDrillNode ? (
      <AttainmentTeamSummaryPage
        node={c.attainmentDrillNode}
        interval={c.attainmentInterval}
        onSelectPerson={c.onSelectPerson}
      />
    ) : (
      <HbrSpinner>Loading attainment…</HbrSpinner>
    ),
  'Leader.AttainmentIndividualContributorPage': (c) =>
    c.attainmentDrillNode ? (
      <AttainmentIndividualContributorPage node={c.attainmentDrillNode} interval={c.attainmentInterval} />
    ) : (
      <HbrSpinner>Loading attainment…</HbrSpinner>
    ),
};

/** HbrSelect sentinel for "no campaign filter" (an empty-string option value renders blank). */
const ALL_CAMPAIGNS = 'all';

export default function LeaderDashboardPage() {
  const sessionEmail = (useSelector((s: RootState) => s.user?.userData?.email) || '').toLowerCase();
  const authz = useSelector((s: RootState) => s.user?.authz);

  const hasGlobalProxy = useMemo(
    () => (authz?.permissions || []).includes(GLOBAL_PROXY_PERMISSION),
    [authz],
  );

  const meEmail = hasGlobalProxy ? PROXY_LEADER_EMAIL : sessionEmail;

  const { data: orgRows = [], isLoading, error } = useOrgChart();
  const org: OrgChartRow[] = orgRows as any;

  // Config-driven routing: the domain tabs and per-(domain, mode) page selection come from the
  // leader_page rows, falling back to a built-in default map when the table is empty (pre-seed).
  const { data: leaderPagesRaw } = useFetchLeaderPages();
  const leaderPages = useMemo(() => resolveLeaderPages(leaderPagesRaw), [leaderPagesRaw]);
  const leaderTabs = useMemo(() => deriveLeaderTabs(leaderPages), [leaderPages]);

  // Campaigns leaders can scope to. The Campaigns domain opens on ALL campaigns; a leader can narrow to
  // one campaign from the picker (`campaignId` undefined = all).
  // Visibility follows the global-proxy pattern used for the rest of the dashboard: a `global.proxy`
  // holder sees every campaign (including drafts/inactive), while a regular leader is restricted to the
  // published + active catalogue (the same filter the SE-facing `CampaignList` applies).
  const campaignParams = useMemo(
    () => (hasGlobalProxy ? undefined : { status: 'published', is_active: true }),
    [hasGlobalProxy],
  );
  const { data: campaignsRaw } = useFetchCampaigns(campaignParams);
  const campaigns = useMemo(() => {
    const rows = (campaignsRaw ?? []) as Array<{
      id: number;
      name: string;
      status?: string;
      is_active?: boolean;
    }>;
    // Defensive client re-check for non-proxy leaders (mirrors CampaignList), so a stale or partial
    // server filter can't leak a draft/inactive campaign into a regular leader's picker.
    return hasGlobalProxy
      ? rows
      : rows.filter((c) => c.status === 'published' && c.is_active !== false);
  }, [campaignsRaw, hasGlobalProxy]);
  const [campaignId, setCampaignId] = useState<number | undefined>(undefined);

  const timePeriod = useFiscalTimePeriod(); // Salesforce tab: opens on the current fiscal quarter
  const campaignTime = useFiscalTimePeriod('all'); // Campaigns tab: opens on all time
  const enablementTime = useFiscalTimePeriod(); // Enablement tab: opens on the current fiscal quarter, like Salesforce and POV
  const povTime = useFiscalTimePeriod(); // POV tab: opens on the current fiscal quarter, like Salesforce

  const [nav, setNav] = useState<LeaderNavState>({
    path: meEmail ? [meEmail] : [],
    mode: 'team',
    domain: 'salesforce',
  });

  useEffect(() => {
    if (!meEmail) return;

    setNav((prev) => {
      if (!prev.path.length) {
        return { path: [meEmail], mode: 'team', domain: prev.domain ?? 'salesforce' };
      }

      if (prev.path[0]?.toLowerCase() !== meEmail) {
        return { path: [meEmail], mode: 'team', domain: prev.domain ?? 'salesforce' };
      }

      return prev;
    });
  }, [meEmail]);

  // Keep the active domain valid against the (possibly async / reconfigured) tab set. If the current
  // domain isn't an enabled tab, snap to the first tab.
  useEffect(() => {
    if (!leaderTabs.length) return;
    if (!leaderTabs.some((t) => t.domain === nav.domain)) {
      setNav((p) => ({ ...p, domain: leaderTabs[0].domain }));
    }
  }, [leaderTabs, nav.domain]);

  const currentEmail = (nav.path[nav.path.length - 1] || '').toLowerCase() || null;

  // The Attainment tab has its OWN reporting structure, discovered level by level from the
  // goal/attainment drill API (one call per person on the path) instead of the User Access org chart,
  // so it keeps a separate path. Synthetic org rows built from those nodes drive the shared filter bar.
  const isAttainment = nav.domain === ATTAINMENT_DOMAIN;
  const [attPath, setAttPath] = useState<string[]>(meEmail ? [meEmail] : []);
  useEffect(() => {
    if (meEmail && attPath[0] !== meEmail) setAttPath([meEmail]);
  }, [meEmail, attPath]);
  // Interval picker: defaults to the date-based current interval, any older one can be chosen. The
  // server only applies `?period=` when it differs from its own default (each person's newest period).
  const [attIntervalLabel, setAttIntervalLabel] = useState<string | null>(null);
  const [knownIntervals, setKnownIntervals] = useState<AttainmentPeriodMeta[]>([]);
  const currentInterval = currentAttainmentPeriod(knownIntervals);
  const attInterval = findPeriodByLabel(knownIntervals, attIntervalLabel) ?? currentInterval;
  // Always ask for the selected interval explicitly: without `?period=` the server answers with each
  // person's newest period, which would show another interval's figures under this interval's name for
  // anyone without a plan in it. (The very first load, before the intervals are known, has no period.)
  const requestPeriod = attInterval?.label ?? undefined;
  const attChain = useAttainmentChain(attPath, isAttainment, requestPeriod);
  const chainIntervals = intervalOptions(attChain.availablePeriods, attChain.nodes);
  const chainIntervalsKey = chainIntervals.map((p) => p.label).join('|');
  useEffect(() => {
    // Remember the list across refetches so the picker doesn't blank while a new interval loads.
    if (chainIntervalsKey) setKnownIntervals(chainIntervals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainIntervalsKey]);
  const attOrg = buildAttainmentOrgRows(attChain.nodes);
  const attDrillNode = attChain.nodes[attChain.nodes.length - 1] ?? null;
  // Org-scope guard: the path may only descend through each loaded node's own direct reports, so a stale
  // or tampered path can never point at someone outside the signed-in user's org. (The server enforces
  // the same rule per request: a non-global leader gets 403 for anyone outside their own subtree.)
  useEffect(() => {
    if (!isAttainment) return;
    for (let i = 0; i < attPath.length - 1; i++) {
      const parent = attChain.nodes[i];
      if (parent && !parent.reports.some((r) => r.email.toLowerCase() === attPath[i + 1])) {
        setAttPath(attPath.slice(0, i + 1));
        return;
      }
    }
  }, [isAttainment, attPath, attChain.nodes]);
  const attMode: LeaderNavState['mode'] =
    attDrillNode && attDrillNode.reports.length === 0 ? 'individual' : 'team';

  // Enablement data is fetched PER NODE, lazily, for the person the dashboard is focused on — a shallow
  // "drill" envelope (own detail + subtree rollup + direct-report summary stubs) rather than the whole
  // org forest at once. Drilling into a report simply refetches for that email. The query is `retry:false`
  // and degrades to no data (its tiles disappear) in local mode or out of scope.
  const { data: educationData } = useEducationCompletionByEmail(currentEmail ?? undefined, {
    timeFilter: enablementTime.filter,
    queryOptions: { enabled: !!currentEmail && !enablementTime.pending },
  });
  const educationNode = educationData?.node ?? null;

  const currentIsManager = useMemo(() => {
    if (!currentEmail) return false;
    return isManagerEmail(org, currentEmail);
  }, [org, currentEmail]);

  useEffect(() => {
    if (!currentEmail) return;

    const isRoot = nav.path.length === 1;

    if (!isRoot && nav.mode === 'team' && !currentIsManager) {
      setNav((p) => ({ ...p, mode: 'individual' }));
    }

    if (!isRoot && nav.mode === 'individual' && currentIsManager) {
      setNav((p) => ({ ...p, mode: 'team' }));
    }
  }, [nav.mode, nav.path.length, currentEmail, currentIsManager]);

  if (!meEmail) return <div style={{ padding: 12 }}>No user email in session.</div>;
  if (isLoading) return <HbrSpinner>Loading org chart…</HbrSpinner>;
  if (error) return <div style={{ padding: 12, color: 'red' }}>{(error as Error).message}</div>;
  if (!currentEmail) return null;

  const onSelectPerson = (email: string) => {
    const e = String(email || '').toLowerCase();
    if (isAttainment) {
      setAttPath((prev) => [...prev, e]);
      return;
    }
    const nextIsMgr = isManagerEmail(org, e);

    setNav((prev) => ({
      ...prev,
      path: [...prev.path, e],
      mode: nextIsMgr ? 'team' : 'individual',
    }));
  };

  return (
    <div className="flex flex-col gap-4 p-4 h-screen min-h-0" style={{ height: '100vh' }}>
      <div style={{ flex: '0 0 auto' }}>
        <LeaderFiltersBar
          orgRows={isAttainment ? attOrg : org}
          state={isAttainment ? { path: attPath, mode: attMode, domain: nav.domain } : nav}
          onChange={(next) => {
            if (next.domain !== nav.domain) setNav((p) => ({ ...p, domain: next.domain }));
            else if (isAttainment) setAttPath(next.path);
            else setNav(next);
          }}
          tabs={leaderTabs}
        />
        {nav.domain === 'salesforce' && (
          <div className="mt-2">
            <TimePeriodSelect
              periodId={timePeriod.periodId}
              onChange={timePeriod.setPeriodId}
              periods={timePeriod.periods}
              loading={timePeriod.pending}
              notice={timePeriod.notice}
            />
          </div>
        )}
        {nav.domain === 'pov' && (
          <div className="mt-2">
            <TimePeriodSelect
              periodId={povTime.periodId}
              onChange={povTime.setPeriodId}
              periods={povTime.periods}
              loading={povTime.pending}
              notice={povTime.notice}
            />
          </div>
        )}
        {nav.domain === 'enablement' && (
          <div className="mt-2">
            <TimePeriodSelect
              periodId={enablementTime.periodId}
              onChange={enablementTime.setPeriodId}
              periods={enablementTime.periods}
              loading={enablementTime.pending}
              notice={enablementTime.notice}
            />
            <div className="hbr-css__text-secondary-sm mt-1">
              A series falls in a period by its due date, or the date it was assigned if it has no due date. Series with
              neither date are always shown.
            </div>
          </div>
        )}
        {isAttainment && (
          <div className="mt-2">
            <AttainmentIntervalBar
              intervals={knownIntervals}
              selected={attInterval}
              current={currentInterval}
              onChange={setAttIntervalLabel}
            />
          </div>
        )}
        {nav.domain === 'campaigns' && (
          <div className="flex items-start gap-3 flex-wrap mt-2">
            {campaigns.length > 0 && (
              <div className="min-w-[320px]">
                <div className="hbr-css__text-primary-xs-semibold mb-1">Campaign</div>
                <HbrSelect
                  value={campaignId == null ? ALL_CAMPAIGNS : String(campaignId)}
                  aria-label="Select campaign"
                  onHbr-change={(e) => {
                    const v = asSingleValue(e.target.value);
                    setCampaignId(v && v !== ALL_CAMPAIGNS ? Number(v) : undefined);
                  }}
                >
                  <HbrOption value={ALL_CAMPAIGNS}>All campaigns</HbrOption>
                  {campaigns.map((c) => (
                    <HbrOption key={c.id} value={String(c.id)}>
                      {c.name}
                    </HbrOption>
                  ))}
                </HbrSelect>
              </div>
            )}
            <TimePeriodSelect
              periodId={campaignTime.periodId}
              onChange={campaignTime.setPeriodId}
              periods={campaignTime.periods}
              loading={campaignTime.pending}
              notice={campaignTime.notice}
            />
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
        {(() => {
          const activePage = findLeaderPage(leaderPages, nav.domain, isAttainment ? attMode : nav.mode);
          const renderer = activePage ? LEADER_PAGE_RENDERERS[activePage.component_key] : undefined;

          if (!renderer) {
            return (
              <div className="hbr-css__text-secondary-sm" style={{ padding: 12 }}>
                This dashboard page is not configured.
              </div>
            );
          }

          if (isAttainment && !attDrillNode && attChain.error) {
            return (
              <div style={{ padding: 12, color: 'red' }}>
                Attainment is unavailable for {attPath[attPath.length - 1]}: {attChain.error.message}
              </div>
            );
          }

          return renderer({
            org,
            currentEmail,
            onSelectPerson,
            campaignId,
            campaignName: campaigns.find((c) => c.id === campaignId)?.name,
            campaignWindow: campaignTime.range,
            campaignTimeShort: TIME_PERIOD_OPTIONS.find((o) => o.id === campaignTime.periodId)?.short ?? '',
            campaignTimePending: campaignTime.pending,
            educationNode,
            educationTimeFilter: enablementTime.filter,
            attainmentDrillNode: attDrillNode,
            attainmentInterval: attInterval,
            timeFilter: timePeriod.filter,
            timeShort: TIME_PERIOD_OPTIONS.find((o) => o.id === timePeriod.periodId)?.short ?? '',
            timePending: timePeriod.pending,
            povTimeFilter: povTime.filter,
            povTimeShort: TIME_PERIOD_OPTIONS.find((o) => o.id === povTime.periodId)?.short ?? '',
            povTimePending: povTime.pending,
          });
        })()}
      </div>
    </div>
  );
}